import { supabase } from "../../../../data/supabase/client";
import { generateCoachingInsights, PRODUCTION_MODEL } from "../../../../services/claude";
import type { Json } from "../../../../types/database.types";
import { logError } from "../../../../utils/error";
import { getTimeRangeFilter } from "../../../../utils/timerange";
import { DEFAULT_MAX_TOKENS, DEFAULT_TEMPERATURE } from "../../constants/modelDefaults";
import { buildCoachingContext } from "../../utils/buildCoachingContext";
import { formatUserNameFromEmail } from "../../utils/formatUserName";
import { generateDefaultCoachingPrompt } from "../../utils/prompts";
import type { GenerateInsightsParams, InsightsActions } from "../coachingGeneratorStore";

export const createInsightsActions = (set: any, get: any): InsightsActions => ({
  setIsGeneratingInsights: (isGenerating: boolean) =>
    set({ isGeneratingInsights: isGenerating }),

  setError: (error: string) => set({ error }),

  clearError: () => set({ error: "" }),

  generateInsights: async (params: GenerateInsightsParams): Promise<string | null> => {
    const {
      attributeExamples,
      attributeStatsContent,
      lensDefinitionContent,
      teamBestPracticesContent,
      selectedUserEmail,
      lensName,
      timeRange,
      timezone,
      tenantId,
      coachingLensPrompt,
      coachingLensTitle,
      coachingLensModel,
      coachingLensTemperature,
      coachingLensMaxTokens
    } = params;

    const state = get();
    const {
      systemPrompt,
      customPrompt,
      selectAllAttributes
    } = state;

    const model = coachingLensModel || PRODUCTION_MODEL.id;
    const temperature = coachingLensTemperature ?? DEFAULT_TEMPERATURE;
    const maxTokens = coachingLensMaxTokens || DEFAULT_MAX_TOKENS;

    set({ isGeneratingInsights: true, error: "" });

    try {
      const availableAttributes = attributeExamples
        .filter(example => example.lowScoring.length > 0 || example.highScoring.length > 0)
        .map(example => example.attribute);

      if (availableAttributes.length === 0) {
        throw new Error("No available attributes to generate insights");
      }

      selectAllAttributes(availableAttributes);

      const attributeNames = availableAttributes.join(", ");
      const defaultPrompt = generateDefaultCoachingPrompt({ lensName, attributeNames, coachingHubPrompt: coachingLensPrompt });

      const context = buildCoachingContext({
        includeAttributeStats: true,
        attributeStatsContent,
        includeLensDefinition: true,
        lensDefinitionContent,
        includeTeamBestPractices: true,
        teamBestPracticesContent,
        attributeExamples,
        selectedUserEmail,
        lensName,
        timeRange,
        timezone
      });

      const agentName = formatUserNameFromEmail(selectedUserEmail);
      const { startDate, endDate } = getTimeRangeFilter(timeRange, timezone);

      const result = await generateCoachingInsights({
        coachingPrompt: customPrompt || defaultPrompt,
        systemPrompt,
        context,
        agentName,
        lensName,
        timeRange,
        temperature,
        maxTokens,
        model,
        coachingPromptTitle: coachingLensTitle
      });

      if (!result.ok || !result.insights) {
        throw new Error(result.error?.message || "Failed to generate insights");
      }

      const { data, error: dbError } = await supabase
        .from("coaching_artifact")
        .insert({
          tenant_id: tenantId as string,
          agent_email: selectedUserEmail,
          start_date: startDate,
          end_date: endDate,
          lens_name: lensName,
          lens_config: {
            temperature,
            maxTokens,
            model,
            selectedAttributes: availableAttributes,
            includeAttributeStats: true,
            includeLensDefinition: true,
            includeTeamBestPractices: true,
            customPrompt: customPrompt || defaultPrompt
          },
          system_prompt: systemPrompt,
          content: result.insights as unknown as Json,
          source_data_filter: {
            timeRange
          }
        })
        .select()
        .single();

      if (dbError) {
        logError("Failed to save coaching artifact", dbError);
        throw dbError;
      }

      set({ isGeneratingInsights: false });
      return data?.id || null;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An error occurred";
      set({ error: errorMessage, isGeneratingInsights: false });
      throw err;
    }
  }
});
